import { useEffect, useRef, useState } from 'react';

interface StagedProgress {
  /** Fill of each stage, 0–100. */
  percents: number[];
  done: boolean;
}

/**
 * Fills `stageCount` bars one after another, each over `secondsPerStage`.
 * When `running` is false the bars stay at a representative still frame.
 */
export const useStagedProgress = (
  stageCount: number,
  secondsPerStage: number,
  running: boolean,
): StagedProgress => {
  const stageMs = Math.max(0.3, secondsPerStage) * 1000;
  const [elapsed, setElapsed] = useState(0);
  const frameRef = useRef<number>(0);

  useEffect(() => {
    if (!running || stageCount === 0) return undefined;
    const total = stageMs * stageCount;
    const start = performance.now();

    const tick = (now: number) => {
      const next = Math.min(total, now - start);
      setElapsed(next);
      if (next < total) frameRef.current = requestAnimationFrame(tick);
    };
    frameRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameRef.current);
  }, [running, stageCount, stageMs]);

  if (!running) {
    return {
      percents: Array.from({ length: stageCount }, (_, i) => (i === 0 ? 100 : i === 1 ? 45 : 0)),
      done: false,
    };
  }

  const percents = Array.from({ length: stageCount }, (_, i) => {
    const local = (elapsed - i * stageMs) / stageMs;
    return Math.round(Math.min(1, Math.max(0, local)) * 100);
  });

  return { percents, done: stageCount > 0 && elapsed >= stageMs * stageCount };
};
